import { ComponentIds, Vector } from '@inter-ikea-core/core';
import { PlannerEcsSystem, ProductType } from '@inter-ikea-storageone/range';
import AutofillSingleWallComponent, { AutoFillDoorTypeEnum } from '../../components/AutofillSingleWallComponent';
import { getAutofillRecommendationFromDataSet } from './RuleSet/AutofillRuleSet';
import { CustomComponentIds } from '@inter-ikea-kvadrat/kvadrat';
/**
 * System responsible for automatically filling a wall with PAX frames based on given configuration.
 * This system handles the placement and alignment of frames along a wall, taking into account
 * wall dimensions, frame specifications, and door types.
 *
 * @implements {WithMarketProducts}
 */
export class AutofillSingleWallSystem extends PlannerEcsSystem {
    constructor(coreApi) {
        super(coreApi, 'AutofillSingleWallSystem', AutofillSingleWallComponent.id);
        this.coreApi = coreApi;
        this.frameProducts = [];
    }
    /**
     * Sets the market products that can be used for autofill.
     * @param marketProducts - The list of market products.
     */
    setMarketProducts(marketProducts) {
        this.frameProducts = marketProducts.filter(p => p.type === ProductType.PAX_FRAME || p.type === ProductType.PAX_CORNER_SOLUTION_ASM);
    }
    /**
     * Called when the component is added to an entity.
     * @param entity - The entity to which the component is added.
     * @param component - The component being added.
     */
    onAdd(entity, component) {
        this.fillScene(component);
    }
    /**
     * Called when the component is updated.
     * @param entity - The entity to which the component is attached.
     * @param oldComponent - The previous component state.
     * @param newComponent - The new component state.
     */
    onUpdate(entity, oldComponent, newComponent) {
        this.fillScene(newComponent);
    }
    /**
     * Called when the component is removed from an entity.
     */
    onRemove() { }
    /**
     * Places frames in the scene according to the autofill configuration.
     * This method:
     * 1. Gets recommendations for frame placement based on wall dimensions and requirements
     * 2. Calculates the starting position for the first frame
     * 3. Places frames sequentially, aligning them to each other
     *
     * @param coreApi - Instance of PlannerCoreApi for scene manipulation
     * @param component - Configuration component containing wall and frame specifications
     */
    fillScene(component) {
        this.coreApi.history.historyAction(() => {
            this.addFramesToScene(this.coreApi, component);
        }, true);
    }
    /**
     * Calculates the starting position for the first frame on the wall.
     * Takes into account:
     * - Wall width
     * - Frame width
     * - Camera position for optimal placement
     *
     * @param wallWidth - Total width of the wall in scene units
     * @param firstFrameWidth - Width of the first frame to be placed
     * @returns Vector representing the optimal starting position
     */
    getStartPosition(wallWidth, firstFrameWidth) {
        const possiblePlacementPoints = this.getPossiblePlacementPoints();
        if (possiblePlacementPoints && possiblePlacementPoints.length > 0) {
            return this.calculateStartingPosition(wallWidth, this.getPlacementFacingCamera(possiblePlacementPoints), firstFrameWidth);
        }
        return new Vector(0, 0, 0);
    }
    /**
     * Retrieves all possible placement points in the scene by analyzing room walls.
     * For each wall, it:
     * 1. Finds wall connectors
     * 2. Gets world positions and transforms
     * 3. Creates placement options with position and orientation vectors
     *
     * @returns Array of possible placement points or undefined if none found
     */
    getPossiblePlacementPoints() {
        const room = this.coreApi.entities.getScene();
        const possiblePlacements = [];
        if (room) {
            const walls = this.coreApi.entities.getByComponent(CustomComponentIds.ROOM_WALL_COMPONENT_ID);
            for (let i = 0; i < walls.length; i++) {
                const wall = walls[i];
                const connectors = wall.getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)?.connectors;
                if (connectors && connectors?.length > 0) {
                    const position = connectors[0].getWorldPositionFromOwnerEntity(wall);
                    const transform = wall.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform;
                    if (position && transform) {
                        possiblePlacements.push({
                            position,
                            forward: transform.up,
                            right: transform.right
                        });
                    }
                }
            }
        }
        return possiblePlacements;
    }
    /**
     * Determines which wall placement faces most directly toward the camera.
     * Uses dot product to calculate the alignment between wall normal and camera direction.
     *
     * @param possiblePlacements - Array of potential placement positions and orientations
     * @returns The placement option that best faces the camera
     */
    getPlacementFacingCamera(possiblePlacements) {
        const cameraDirectionNormalV3 = this.coreApi.camera.getActiveCamera().forward;
        const cameraDirectionNormal = new Vector(cameraDirectionNormalV3.x, cameraDirectionNormalV3.y, cameraDirectionNormalV3.z);
        let placementMostlyFacingCamera = { p: possiblePlacements[0], diff: 1 };
        possiblePlacements.forEach(p => {
            // Calculate the absolute difference between the wall rotation and the camera
            const diff = p.forward.dot(cameraDirectionNormal);
            if (diff < placementMostlyFacingCamera.diff) {
                placementMostlyFacingCamera = { p, diff };
            }
        });
        return placementMostlyFacingCamera.p;
    }
    /**
     * Calculates the exact starting position for the first frame based on wall parameters.
     * Takes into account:
     * - Wall dimensions
     * - Frame width
     * - Wall orientation
     *
     * @param wallWidth - Width of the wall
     * @param possiblePlacement - Selected placement configuration
     * @param firstFrameWidth - Width of the first frame
     * @returns Vector representing the calculated starting position
     */
    calculateStartingPosition(wallWidth, possiblePlacement, firstFrameWidth) {
        return possiblePlacement.position.plus(possiblePlacement.right.times(-wallWidth / 2 + firstFrameWidth / 2));
    }
    /**
     * Manages the process of adding frames to the scene based on recommendations.
     * Workflow:
     * 1. Clears existing entities
     * 2. Gets frame recommendations
     * 3. Places initial frame
     * 4. Sequentially adds remaining frames
     *
     * @param coreApi - Core API instance for scene manipulation
     * @param component - Component containing autofill configuration
     */
    addFramesToScene(coreApi, component) {
        coreApi.entities.getAll().forEach(e => coreApi.entities.delete(e));
        const recommendations = getAutofillRecommendationFromDataSet(this.frameProducts, component.frameDepth, component.design, component.frameHeight, component.doorType === AutoFillDoorTypeEnum.SLIDING, component.wallWidth);
        if (recommendations?.length > 0) {
            const startPosition = this.getStartPosition(component.wallWidth, recommendations[0].frame.communicatedDimensions.width);
            const startEntity = this.coreApi
                .getServices()
                .productManager.createEntity(recommendations[0].frame.coreProductId);
            if (startEntity) {
                const startResult = this.coreApi.placementService.snapToPosition(startEntity, startPosition, false);
                if (startResult.valid && startResult.placedEntity) {
                    let prev = startResult.placedEntity;
                    for (let i = 1; i < recommendations.length; i++) {
                        const result = this.addFrame(prev, recommendations[i].frame.coreProductId);
                        if (result && result.valid) {
                            prev = result.placedEntity;
                        }
                        else if (result && !result.valid) {
                            // eslint-disable-next-line no-console
                            console.warn('could not add frame', result);
                            return;
                        }
                    }
                }
            }
        }
        component.finished();
    }
    /**
     * Places a new frame in the scene aligned to an existing frame.
     * Uses connector-based alignment to ensure proper positioning.
     *
     * @param hostEntity - Existing frame entity to align to
     * @param frameToAddCoreProductId - Product ID of the new frame to add
     * @returns Result of the placement operation
     */
    addFrame(hostEntity, frameToAddCoreProductId) {
        const newEntity = this.coreApi
            .getServices()
            .productManager.createEntity(frameToAddCoreProductId);
        if (newEntity) {
            return this.coreApi.placementService.alignToTargetSpecificConnectors(newEntity, 'align-left', hostEntity, 'align-right');
        }
    }
}
//# sourceMappingURL=AutofillSingleWallSystem.js.map